import { createTheme, alpha } from '@material-ui/core/styles';
import { ptBR } from '@material-ui/core/locale';
import { whichClientCompanyIsTheDocument } from '../utils/customerCompanies';

declare module '@material-ui/core/styles' {
  interface Theme {
    gradient: { company: React.CSSProperties['background'] };
    setCompanyTheme: (company: string) => void;
  }
  interface ThemeOptions {
    gradient?: { company: React.CSSProperties['background'] };
    setCompanyTheme?: (company: string) => void;
  }
}

const companiesCustomTheme = {
  default: {
    primary: '#570864',
    secondary: '#01579b',
    gradient:
      'transparent linear-gradient(90deg, #01579BBF 0%, #3B2276CC 49%, #570864CC 100%) 0% 0% no-repeat padding-box',
  },

  ativa: {
    primary: '#0E52A0',
    secondary: '#48AE3D',
    gradient:
      'transparent linear-gradient(90deg, #0D52A0 0%, #0D52A0 100%) 0% 0% no-repeat padding-box',
    //'transparent linear-gradient(90deg, #48AE3D 0%, #0E52A0 49%, #7DA1C8 100%) 0% 0% no-repeat padding-box',
  },
};

// Redesign "Apple": fonte do sistema com tracking por tamanho, cantos
// arredondados, sombras em camadas, chrome translúcido e botões sem caixa
// alta. Cores da marca por empresa permanecem as mesmas.
const SYSTEM_FONT =
  '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", Roboto, system-ui, sans-serif';

const INK = '#1A2430';
const INK_SOFT = '#5A6B7A';
const CANVAS_GRADIENT =
  'linear-gradient(145deg, #F4F6F9 0%, #EEF2F6 60%, #EAF0F5 100%)';

const SOFT_SHADOW_SM =
  '0 1px 2px rgba(16, 24, 40, 0.04), 0 6px 20px rgba(16, 24, 40, 0.05)';
const SOFT_SHADOW_MD =
  '0 1px 2px rgba(16, 24, 40, 0.04), 0 8px 24px rgba(16, 24, 40, 0.06)';
const SOFT_SHADOW_LG =
  '0 2px 4px rgba(16, 24, 40, 0.05), 0 18px 50px rgba(16, 24, 40, 0.18)';

export const theme = ({
  companyDocument,
  setCompanyTheme,
}: {
  companyDocument?: string;
  setCompanyTheme: (company: string) => void;
}) => {
  const companyCustomTheme =
    companiesCustomTheme[whichClientCompanyIsTheDocument(companyDocument)];

  const primary = companyCustomTheme.primary;

  return createTheme(
    {
      palette: {
        primary: {
          main: companyCustomTheme.primary,
          contrastText: '#FFF',
        },
        secondary: {
          main: companyCustomTheme.secondary,
          contrastText: '#FFF',
        },
        background: {
          default: '#F2F4F7',
        },
        text: {
          primary: INK,
        },
      },

      setCompanyTheme,

      gradient: {
        company: companyCustomTheme.gradient,
      },

      shape: {
        borderRadius: 10,
      },

      typography: {
        fontFamily: SYSTEM_FONT,
        h1: { letterSpacing: '-0.02em', fontWeight: 700 },
        h2: { letterSpacing: '-0.02em', fontWeight: 700 },
        h3: { letterSpacing: '-0.02em', fontWeight: 700 },
        h4: { letterSpacing: '-0.02em', fontWeight: 700 },
        h5: { letterSpacing: '-0.01em', fontWeight: 700 },
        h6: { letterSpacing: '-0.01em', fontWeight: 600 },
        subtitle2: { fontWeight: 600 },
        button: {
          textTransform: 'none',
          fontWeight: 600,
          letterSpacing: 0,
        },
      },

      overrides: {
        MuiCssBaseline: {
          '@global': {
            body: {
              background: CANVAS_GRADIENT,
              backgroundAttachment: 'fixed',
            },
            '@media (prefers-reduced-motion: reduce)': {
              '*, *::before, *::after': {
                animationDuration: '0.01ms !important',
                transitionDuration: '0.01ms !important',
              },
            },
          },
        },

        MuiButton: {
          root: {
            borderRadius: 10,
            transition:
              'transform 100ms ease-out, background-color 150ms ease-out, box-shadow 150ms ease-out',
            // Feedback físico no toque: responde no press, não só no clique.
            '&:active': {
              transform: 'scale(0.97)',
            },
          },
          contained: {
            boxShadow: '0 1px 2px rgba(16, 24, 40, 0.25)',
            '&:hover': {
              boxShadow: '0 2px 6px rgba(16, 24, 40, 0.25)',
            },
            '&$disabled': {
              boxShadow: 'none',
            },
          },
        },

        MuiIconButton: {
          root: {
            transition: 'transform 100ms ease-out, background-color 150ms ease-out',
            '&:active': {
              transform: 'scale(0.94)',
            },
          },
        },

        MuiPaper: {
          rounded: {
            borderRadius: 14,
          },
          elevation1: { boxShadow: SOFT_SHADOW_SM },
          elevation2: { boxShadow: SOFT_SHADOW_SM },
          elevation3: { boxShadow: SOFT_SHADOW_MD },
          elevation4: { boxShadow: SOFT_SHADOW_MD },
          elevation8: { boxShadow: SOFT_SHADOW_LG },
        },

        MuiOutlinedInput: {
          root: {
            borderRadius: 10,
            backgroundColor: '#FBFCFE',
            '& $notchedOutline': {
              borderColor: '#E3E8EF',
            },
            '&:hover $notchedOutline': {
              borderColor: alpha(primary, 0.4),
            },
          },
        },

        MuiTableCell: {
          root: {
            borderBottom: '1px solid #F2F4F7',
          },
          head: {
            color: '#64748B',
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: '0.02em',
          },
        },

        MuiTableRow: {
          root: {
            '&$hover:hover': {
              backgroundColor: alpha(primary, 0.035),
            },
          },
        },

        MuiChip: {
          root: {
            fontWeight: 600,
          },
        },

        MuiDialog: {
          paper: {
            borderRadius: 16,
            boxShadow: SOFT_SHADOW_LG,
          },
        },

        MuiTab: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
          },
        },

        MuiTooltip: {
          tooltip: {
            borderRadius: 8,
            backgroundColor: alpha(INK, 0.92),
            fontSize: 12,
          },
        },

        // Topbar de vidro: conteúdo rola por baixo; a divisória de 1px vira
        // um "scroll edge" em gradiente (::after).
        MuiAppBar: {
          colorPrimary: {
            backgroundColor: 'rgba(255, 255, 255, 0.72)',
            backdropFilter: 'blur(20px) saturate(180%)',
            color: INK,
            boxShadow: 'none',
            borderTop: '1px solid rgba(255, 255, 255, 0.5)',
            '&::after': {
              content: '""',
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              height: 14,
              pointerEvents: 'none',
              background:
                'linear-gradient(rgba(234, 240, 245, 0.9), rgba(234, 240, 245, 0))',
            },
          },
        },

        // Sidebar: material translúcido mais pesado, seleção em pílula.
        MuiDrawer: {
          paper: {
            backgroundColor: 'rgba(250, 251, 253, 0.82)',
            backdropFilter: 'blur(24px) saturate(160%)',
            borderRight: '1px solid rgba(16, 24, 40, 0.07)',
            '& .MuiListItem-button': {
              borderRadius: 9,
              margin: '1px 8px',
              width: 'auto',
              paddingTop: 6,
              paddingBottom: 6,
            },
            '& .MuiListItem-button.Mui-selected': {
              backgroundColor: alpha(primary, 0.11),
              color: primary,
              '&:hover': {
                backgroundColor: alpha(primary, 0.16),
              },
              '& .MuiListItemIcon-root': {
                color: primary,
              },
              '& .MuiListItemText-primary': {
                fontWeight: 600,
              },
            },
            '& .MuiListItemIcon-root': {
              minWidth: 42,
              color: INK_SOFT,
            },
            '& .MuiListItemText-primary': {
              fontSize: 13.5,
            },
          },
        },
      },
    },
    ptBR
  );
};
